import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Bug, Sparkles, Rocket, AlertTriangle, Eye, Bell } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { kindDef, notificationTarget, type NotificationCategory, type NotificationGroup } from '@pcp/shared';
import type { AppNotification } from '../../lib/api';
import { readerLocale } from '../../lib/locale';
import { linkClickProps } from '../../lib/linkClick';

// ─── One notification, shared by the tray and the history page ───
//
// Both surfaces render the same facts in the same colours; only the density
// differs. Keeping them in one file is what stops the tray and the page drifting
// into two different vocabularies for the same row. (f-01806)

/** The glyph + colour a kind is drawn with. Bug requests are the only kind that
 *  earns the danger colour — everything else reads as information, and a feed
 *  where half the rows are red is a feed nobody scans. */
export function kindVisual(kind: string, entityId?: string | null): { Glyph: LucideIcon; color: string; tint: string } {
  const category = kindDef(kind)?.category as NotificationCategory | undefined;
  // `request.implemented` and `request.status` are type-agnostic kinds, so the
  // key alone cannot say whether this is a bug. The refId can: it is the entity
  // id we already store and route on, and `b-01796` is a bug by definition. A
  // row headed "Bug request implemented" wearing the feature colour is exactly
  // the kind of small lie that makes a feed feel unreliable.
  const isBug = kind.startsWith('request.bug') || (!!entityId && /^b-\d+$/i.test(entityId));
  if (isBug) {
    return { Glyph: Bug, color: 'var(--accent-error, #c75c5c)', tint: 'var(--accent-error-soft, rgba(199,92,92,0.14))' };
  }
  switch (category) {
    case 'releases':
      return { Glyph: Rocket, color: 'var(--accent-success, #3fb950)', tint: 'var(--accent-success-soft, rgba(63,185,80,0.14))' };
    case 'system':
      return kind === 'sync.error'
        ? { Glyph: AlertTriangle, color: 'var(--accent-warning, #b8860b)', tint: 'var(--accent-warning-soft, rgba(184,134,11,0.14))' }
        : { Glyph: Eye, color: 'var(--text-secondary)', tint: 'var(--bg-hover)' };
    case 'requests':
      return { Glyph: Sparkles, color: 'var(--accent-primary)', tint: 'var(--accent-primary-soft)' };
    default:
      return { Glyph: Bell, color: 'var(--accent-primary)', tint: 'var(--accent-primary-soft)' };
  }
}

/** Short, locale-correct time for a row. Same day → clock; this year → day+month;
 *  older → include the year. Never a hardcoded locale. */
export function notificationTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString(readerLocale(), { hour: '2-digit', minute: '2-digit' });
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(readerLocale(), { day: 'numeric', month: 'short' });
  }
  return d.toLocaleDateString(readerLocale(), { dateStyle: 'medium' });
}

/** The day heading a row groups under. */
export function notificationDayLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(readerLocale(), { dateStyle: 'medium' });
}

/** The tag each reason renders. `subscribed` is the unremarkable case and says
 *  nothing; `parent` is a child of a Feature of yours (f-01956) — the row's own
 *  title says which Feature, so the tag only says it came through one. */
const REASON_TAG = new Map<string, { label: string; accent: boolean; title?: string }>([
  ['followed', { label: 'Following', accent: true }],
  ['mine', { label: 'Yours', accent: false }],
  ['parent', { label: 'Via Feature', accent: false, title: 'A child item of a Feature you follow, created or are assigned to' }],
]);

/** Why this row reached this person. A reason with no tag renders nothing. */
export function ReasonTag({ reason }: { reason: string }) {
  const tag = REASON_TAG.get(reason);
  if (!tag) return null;
  return (
    <span title={tag.title} style={{
      display: 'inline-flex', alignItems: 'center', padding: '0 5px', borderRadius: 'var(--radius-sm, 4px)',
      fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      background: tag.accent ? 'var(--accent-primary-soft)' : 'var(--bg-hover)',
      color: tag.accent ? 'var(--accent-primary)' : 'var(--text-secondary)',
    }}>
      {tag.label}
    </span>
  );
}

export function NotificationIcon({ kind, entityId, size = 26 }: { kind: string; entityId?: string | null; size?: number }) {
  const { Glyph, color, tint } = kindVisual(kind, entityId);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      width: size, height: size, borderRadius: 'var(--radius-sm, 6px)', background: tint, color,
    }}>
      <Glyph size={Math.round(size * 0.54)} />
    </span>
  );
}

/** How the tray opens a record. Every row is a real link to it (f-02008):
 *  `onActivate` runs for ANY open — a Ctrl/Cmd or middle click into a new tab marks
 *  the record read too — and `onOpen` only for a plain click, which navigates this
 *  tab and closes the tray. A modified click leaves the tray open where it was. */
interface OpenHandlers {
  onActivate: () => void;
  onOpen: () => void;
}

/** The tray's row. The history page draws its own grid row instead — same
 *  vocabulary, different density. */
export function NotificationRow({ n, onActivate, onOpen }: { n: AppNotification } & OpenHandlers) {
  const unread = !n.readAt;
  return (
    <a
      {...linkClickProps({ href: notificationTarget(n), onActivate, onOpen })}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', width: '100%', textAlign: 'left',
        padding: 'var(--space-2)', borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
        background: unread ? 'var(--accent-primary-soft)' : 'transparent', color: 'inherit', textDecoration: 'none',
      }}
    >
      <NotificationIcon kind={n.kind} entityId={n.entityId} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 600, lineHeight: 1.35 }}>{n.title}</span>
        {n.body && (
          <span style={{
            display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{n.body}</span>
        )}
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: '10.5px', color: 'var(--text-tertiary)' }}>
          <span>{notificationTime(n.createdAt)}</span>
          <ReasonTag reason={n.reason} />
        </span>
      </span>
      {unread && (
        <span aria-hidden style={{
          width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 8,
          background: 'var(--accent-primary)',
        }} />
      )}
    </a>
  );
}

/**
 * A record with more than one unread notification, drawn as a deck.
 *
 * The copy is deliberately UNCHANGED from a single row. The producers already
 * write item-first titles — "Ticket 21095: replied" over the ticket's subject,
 * "Bug 21188 moved to Active" over the work item's title — so a deck needs no
 * invented heading, and inventing one would mean guessing an item name for kinds
 * whose body is not one. What the deck adds is depth and a number.
 *
 * Two hit areas, and the split is the point: the body opens the record (the
 * common case, one tap), the count chip fans the deck. Opening either way marks
 * every row in the group read — the tray is unread-only, so a half-read group
 * comes back one row shorter and reads as the tray losing things. (f-01816)
 */
export function NotificationDeck({
  group, onActivate, onOpen,
}: {
  group: NotificationGroup<AppNotification>;
} & OpenHandlers) {
  // `onActivate` marks the whole group read and `onOpen` goes to the record — the
  // caller holds the group, so there is nothing to hand back.
  const [open, setOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const n = group.newest;
  const ease = reduceMotion ? { duration: 0 } : { duration: 0.22, ease: [0.16, 1, 0.3, 1] as const };
  // Every event in a deck resolves to the same record, so the newest one's target
  // is the group's — one link props object serves the card and every fanned line.
  const link = linkClickProps({ href: notificationTarget(n), onActivate, onOpen });

  // A record with one notification is not a deck. Rendered by the same component
  // so the tray never has to ask, and so the two paths cannot drift apart.
  if (group.count < 2) return <NotificationRow n={n} onActivate={onActivate} onOpen={onOpen} />;

  return (
    <div className={`notification-deck${open ? ' is-open' : ''}`}>
      {/* The layers behind the card. NEUTRAL, not accent: an unread row is a 10%
          tint, and a tint on a tint is invisible — the depth has to step through
          the grey ramp to be seen at all. They also sit BEHIND an opaque card;
          a translucent card would let them show straight through it. */}
      <motion.span
        aria-hidden
        className="notification-deck-layer is-l2"
        animate={{ opacity: open ? 0 : 0.72, y: open ? 8 : 0 }}
        transition={ease}
      />
      <motion.span
        aria-hidden
        className="notification-deck-layer is-l1"
        animate={{ opacity: open ? 0 : 1, y: open ? 8 : 0 }}
        transition={ease}
      />

      <div className="notification-deck-card">
        <a className="notification-deck-open" {...link}>
          <NotificationIcon kind={n.kind} entityId={n.entityId} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 600, lineHeight: 1.35 }}>{n.title}</span>
            {n.body && (
              <span style={{
                display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 1,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{n.body}</span>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: '10.5px', color: 'var(--text-tertiary)' }}>
              <span>{notificationTime(n.createdAt)}</span>
              <ReasonTag reason={n.reason} />
            </span>
          </span>
        </a>
        <button
          type="button"
          className="notification-deck-count"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open
            ? `Collapse the ${group.count} updates`
            : `Show the ${group.count - 1} older update${group.count === 2 ? '' : 's'}`}
        >
          {group.count}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="fan"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={ease}
            style={{ overflow: 'hidden' }}
          >
            <div className="notification-deck-fan">
              {/* The cards UNDERNEATH — slice(1), not the whole group. The newest
                  is already on screen as the card itself, and rendering it again
                  as the first child put the same title and the same timestamp on
                  two adjacent lines, which reads as a bug. Fanning a deck shows
                  what was hidden by the top card; the chip still counts the whole
                  deck, and the whole deck is what is on screen. */}
              {group.items.slice(1).map((item) => (
                // Every event resolves to the same record, so a child is not a
                // different destination — it is the same one, reached from the
                // line that explains why you are going there.
                <a key={item.id} className="notification-deck-event" {...link}>
                  <span className="notification-deck-event-title">{item.title}</span>
                  <span className="notification-deck-event-time">{notificationTime(item.createdAt)}</span>
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
